/**
 * Signal indicators (FLLHH, FBO, Wave) and LRBnd band data.
 *
 * Each signal computation returns `{ buys: number[], shorts: number[] }` where
 * the numbers are bar `time`s (UNIX seconds, IST-shifted, matching the chart).
 * Markers are emitted only on closed bars — pass closed-only candles to keep
 * the output repaint-safe. The chart's `latestCandle` (forming bar) MUST NOT
 * be in the input array.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles, ema, type CandleType } from './candleTransforms';
import { computePocPerPeriod, CprPeriod } from './dailyBlastIndicators';
import { isInBlastWindow, type BlastWindow } from './blast';

export interface SignalSet {
  buys: number[];   // bar times (s) where a BUY marker should be placed
  shorts: number[]; // bar times (s) where a SHORT marker should be placed
}

const EMPTY: SignalSet = { buys: [], shorts: [] };

/* ─────────────────────────────────────────────────────────────────────────
 * LR_HAMA_Band — average of SLR (CSL) and HAMA open/close clouds
 *
 * Replaces the old LRBnd which was only the SLR (CSL) open/close band.
 * Now mirrors DailyBlast Pine `Avg SLR+HAMA O/C Cloud`:
 *
 *   slr_top   = max(slr_o2,  slr_c2)         // SLR = CSL open/close
 *   slr_bot   = min(slr_o2,  slr_c2)
 *   hama_open = ema((open[1]+close[1])/2, 45)
 *   hama_close= ema((o+h+l+c)/4,         40)
 *   hama_top  = max(hama_open, hama_close)
 *   hama_bot  = min(hama_open, hama_close)
 *   avg_top   = (slr_top + hama_top) / 2
 *   avg_bot   = (slr_bot + hama_bot) / 2
 *
 * The function returns BandPoint[] of (avg_top, avg_bot) per bar.
 * Independent of the chart's display candle type — always computed from
 * the raw OHLC the caller supplies.
 * ──────────────────────────────────────────────────────────────────────── */
export interface BandPoint { time: number; upper: number; lower: number; }

const HAMA_OPEN_LEN  = 45;
const HAMA_CLOSE_LEN = 40;

export function computeLRHamaBand(closedCandles: CandleRow[]): BandPoint[] {
  if (!closedCandles.length) return [];
  // SLR top/bot via CSL transform.
  const csl = transformCandles(closedCandles, 'CSL');
  const cslByTime = new Map<number, { o: number; c: number }>();
  for (const c of csl) {
    if (Number.isFinite(c.open) && Number.isFinite(c.close)) {
      cslByTime.set(c.time, { o: c.open, c: c.close });
    }
  }

  // HAMA on raw OHLC — uses previous-bar (open+close)/2 as hama_open's source
  // and current-bar hlc4 as hama_close's source.
  const n = closedCandles.length;
  const hamaOpenSrc: number[] = new Array(n);
  const hamaCloseSrc: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const cur = closedCandles[i];
    const prev = i > 0 ? closedCandles[i - 1] : cur;
    hamaOpenSrc[i]  = (prev.open + prev.close) / 2;
    hamaCloseSrc[i] = (cur.open + cur.high + cur.low + cur.close) / 4;
  }
  const hamaOpen  = ema(hamaOpenSrc,  HAMA_OPEN_LEN);
  const hamaClose = ema(hamaCloseSrc, HAMA_CLOSE_LEN);

  const out: BandPoint[] = [];
  for (let i = 0; i < n; i++) {
    const cur = closedCandles[i];
    const slr = cslByTime.get(cur.time);
    if (!slr) continue;
    const ho = hamaOpen[i];
    const hc = hamaClose[i];
    if (ho == null || hc == null || !Number.isFinite(ho) || !Number.isFinite(hc)) continue;
    const slrTop = Math.max(slr.o, slr.c);
    const slrBot = Math.min(slr.o, slr.c);
    const hamaTop = Math.max(ho, hc);
    const hamaBot = Math.min(ho, hc);
    const avgTop = (slrTop + hamaTop) / 2;
    const avgBot = (slrBot + hamaBot) / 2;
    out.push({ time: cur.time, upper: avgTop, lower: avgBot });
  }
  return out;
}

/** Backwards-compat alias — old call sites can keep importing `computeLRBndBand`
 *  and will receive the new SLR+HAMA Avg band. */
export const computeLRBndBand = computeLRHamaBand;

/* ─────────────────────────────────────────────────────────────────────────
 * FLLHH — Failed Lower Low / Higher High
 *
 * Always operates on CSL candles, computed once from the *raw* OHLC the
 * caller provides. The CSL transform preserves source bar times, so the
 * emitted signal times equal raw bar times — which in turn equal the
 * display-bar times for Regular / HA / CSL / CLH.
 *
 * Logic (round 24 update — strict comparisons + alternation gate):
 *   - Color rule: green if close ≥ open, else red.
 *   - Walk closed bars grouping consecutive same-color runs; track each
 *     run's min-low and max-high.
 *   - When an opposite-color bar appears, the prior run is "completed":
 *       BUY  if just-completed red run's low  is **strictly greater than**
 *            the previous completed red run's low (failed lower low —
 *            sellers tried again and could not break the prior trough).
 *       SHORT if just-completed green run's high is **strictly less than**
 *            the previous completed green run's high (failed higher high).
 *     A tied low/high no longer fires (the user's spec: "could not break
 *     below" — equal isn't "broke", but equal also isn't "failed").
 *   - Alternation gate: after a BUY, no further BUYs are emitted until a
 *     SHORT has fired (and vice versa). Stops same-side signal stacking
 *     across consecutive groups when conditions keep being satisfied.
 *   - Marker time = the confirming opposite-color bar's time. Repaint-safe.
 * ──────────────────────────────────────────────────────────────────────── */
export function computeFLLHHSignals(closedCandles: CandleRow[]): SignalSet {
  if (closedCandles.length < 4) return EMPTY;
  const csl = transformCandles(closedCandles, 'CSL');
  if (!csl.length) return EMPTY;

  const buys: number[] = [];
  const shorts: number[] = [];

  type Run = { color: 'red' | 'green' | null; lowMin: number; highMax: number };
  let cur: Run = { color: null, lowMin: Number.POSITIVE_INFINITY, highMax: Number.NEGATIVE_INFINITY };
  let lastRedLow: number | null = null;
  let lastGreenHigh: number | null = null;
  // Alternation gate — the last side actually emitted; null until one fires.
  let lastEmitted: 'BUY' | 'SHORT' | null = null;

  for (const bar of csl) {
    if (!Number.isFinite(bar.open) || !Number.isFinite(bar.close)) continue;
    const color: 'red' | 'green' = bar.close >= bar.open ? 'green' : 'red';
    if (cur.color == null) {
      cur = { color, lowMin: bar.low, highMax: bar.high };
      continue;
    }
    if (color === cur.color) {
      if (bar.low < cur.lowMin) cur.lowMin = bar.low;
      if (bar.high > cur.highMax) cur.highMax = bar.high;
      continue;
    }
    // Opposite colour — prior run `cur` is now completed.
    if (cur.color === 'red') {
      // STRICT > : equal-low is NOT a failed-lower-low.
      if (lastRedLow != null && cur.lowMin > lastRedLow && lastEmitted !== 'BUY') {
        buys.push(bar.time);
        lastEmitted = 'BUY';
      }
      lastRedLow = cur.lowMin;
    } else if (cur.color === 'green') {
      if (lastGreenHigh != null && cur.highMax < lastGreenHigh && lastEmitted !== 'SHORT') {
        shorts.push(bar.time);
        lastEmitted = 'SHORT';
      }
      lastGreenHigh = cur.highMax;
    }
    cur = { color, lowMin: bar.low, highMax: bar.high };
  }

  return { buys, shorts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * FBO — False Breakout (port of Zeiierman's False_BO.txt)
 *
 * Operates on raw OHLC. Defaults baked in per the user's spec:
 *   minperiod = 5, maxperiod = 5, length = 10, maType = '💎' (no smoothing),
 *   aggressive = false. Only `period` is configurable.
 *
 * State machine (matches the pinescript verbatim):
 *   hi[i] = highest(high, period); lo[i] = lowest(low, period)
 *   condHi = hi > hi[1] && hi[1] <= hi[2]   (new sustained-high)
 *   condLo = lo < lo[1] && lo[1] >= lo[2]   (new sustained-low)
 *   When condHi: count toward negative; index push; val = low.
 *   When condLo: count toward positive; index push; val = high.
 *
 *   falsebreakoutup   = count<-1 AND crossunder(close, val) AND windowGuards
 *     → SHORT (upside fakeout, price reclaimed below the validation level)
 *   falsebreakoutdown = count>1  AND crossover(close, val)  AND windowGuards
 *     → BUY  (downside fakeout)
 * ──────────────────────────────────────────────────────────────────────── */
export function computeFBOSignals(closedCandles: CandleRow[], period: number): SignalSet {
  const prd = Math.max(2, Math.min(100, Math.floor(period) || 20));
  const minperiod = 5;
  const maxperiod = 5;
  const n = closedCandles.length;
  if (n < prd + 2) return EMPTY;

  // Rolling highest(high, prd) / lowest(low, prd). O(n*prd) is fine here for
  // the chart's typical 300-bar window.
  const hi: number[] = new Array(n).fill(NaN);
  const lo: number[] = new Array(n).fill(NaN);
  for (let i = prd - 1; i < n; i++) {
    let h = closedCandles[i].high;
    let l = closedCandles[i].low;
    for (let k = i - prd + 1; k <= i; k++) {
      if (closedCandles[k].high > h) h = closedCandles[k].high;
      if (closedCandles[k].low < l) l = closedCandles[k].low;
    }
    hi[i] = h;
    lo[i] = l;
  }

  const buys: number[] = [];
  const shorts: number[] = [];

  // Replicated pinescript `var` state.
  let val: number = NaN;
  const idx: [number, number] = [0, 0]; // index[0] = newest, index[1] = previous
  let count = 0;

  // closes[] for cross detection — we process bar-by-bar.
  for (let i = 0; i < n; i++) {
    const bar = closedCandles[i];
    const closePrev = i > 0 ? closedCandles[i - 1].close : bar.close;

    // Detect new breakout structure on this closed bar.
    if (i >= prd + 1 && Number.isFinite(hi[i]) && Number.isFinite(hi[i - 1]) && Number.isFinite(hi[i - 2])) {
      const condHi = hi[i] > hi[i - 1] && hi[i - 1] <= hi[i - 2];
      const condLo = lo[i] < lo[i - 1] && lo[i - 1] >= lo[i - 2];
      if (condHi) {
        if (count > 0) count = 0;
        count = count - 1;
        val = bar.low;
        idx[1] = idx[0];
        idx[0] = i;
      }
      if (condLo) {
        if (count < 0) count = 0;
        count = count + 1;
        val = bar.high;
        idx[1] = idx[0];
        idx[0] = i;
      }
    }

    // Trigger detection — needs val + at least one prior close to detect a cross.
    if (Number.isFinite(val) && i > 0) {
      const minbars = idx[1] + minperiod < idx[0];
      const maxvalid = i - maxperiod <= idx[0];
      const breakdown = closePrev >= val && bar.close < val; // crossunder(close, val)
      const breakup   = closePrev <= val && bar.close > val; // crossover (close, val)
      const falsebreakoutup   = count < -1 && breakdown && maxvalid && minbars; // SHORT
      const falsebreakoutdown = count > 1  && breakup   && maxvalid && minbars; // BUY
      if (falsebreakoutup) {
        shorts.push(bar.time);
        count = 0;
      } else if (falsebreakoutdown) {
        buys.push(bar.time);
        count = 0;
      }
    }
  }

  return { buys, shorts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * Wave — WaveTrend with Crosses (port of LazyBear's WaveTrend.txt)
 *
 * Operates on raw HLC3. Configurable: n1 (Channel Length, default 10),
 * n2 (Average Length, default 21). Hardcoded thresholds per the user's
 * choice: obLevel1 = 60, osLevel1 = -60.
 *
 *   ap  = (high + low + close) / 3
 *   esa = ema(ap, n1)
 *   d   = ema(abs(ap − esa), n1)
 *   ci  = (ap − esa) / (0.015 × d)
 *   tci = ema(ci, n2);   wt1 = tci;   wt2 = sma(wt1, 4)
 *
 *   BUY   = crossover(wt1, wt2)  AND wt2[i] ≤ -60
 *   SHORT = crossunder(wt1, wt2) AND wt2[i] ≥  60
 * ──────────────────────────────────────────────────────────────────────── */
export interface WaveSeries { wt1: number[]; wt2: number[]; warmup: number }

/**
 * The WaveTrend oscillator itself (wt1 and its 4-bar SMA wt2).
 *
 * Extracted so the lower-pane curve and the BUY/SHORT arrows are the SAME
 * numbers — computeWaveSignals below calls it. Returned as its own type rather
 * than widened onto SignalSet, because SignalSet is shared with FLLHH, FBO and
 * ASH and none of those has a series.
 *
 * Scale note, measured across 60 Delta symbols: wt1 runs about -69..+71 (p1..p99)
 * and reaches -100 on 0.00% of bars. So its +/-60 levels are NOT comparable to
 * BBR's +/-100 by value — only by rarity (both tag roughly 5.8% of bars). Any
 * chart that draws the two together has to align them, or the comparison is
 * meaningless. TradingChart does that by pinning the two price scales, never by
 * rescaling these numbers.
 */
export function computeWaveSeries(
  closedCandles: CandleRow[],
  channelLength: number,
  averageLength: number,
): WaveSeries {
  const n1 = Math.max(2, Math.floor(channelLength) || 10);
  const n2 = Math.max(2, Math.floor(averageLength) || 21);
  const N = closedCandles.length;
  const warmup = n1 + n2 + 4;
  if (N < n1 + n2 + 6) return { wt1: [], wt2: [], warmup };

  const ap = closedCandles.map(c => (c.high + c.low + c.close) / 3);
  const esa = ema(ap, n1);
  const absDiff = ap.map((v, i) => Math.abs(v - esa[i]));
  const d = ema(absDiff, n1);
  const ci: number[] = new Array(N);
  for (let i = 0; i < N; i++) {
    const denom = 0.015 * d[i];
    ci[i] = denom !== 0 ? (ap[i] - esa[i]) / denom : 0;
  }
  const wt1 = ema(ci, n2);
  // sma(wt1, 4)
  const wt2: number[] = new Array(N);
  for (let i = 0; i < N; i++) {
    if (i < 3) { wt2[i] = wt1[i]; continue; }
    wt2[i] = (wt1[i] + wt1[i - 1] + wt1[i - 2] + wt1[i - 3]) / 4;
  }
  return { wt1, wt2, warmup };
}

export function computeWaveSignals(
  closedCandles: CandleRow[],
  channelLength: number,
  averageLength: number,
): SignalSet {
  const obLevel = 60;
  const osLevel = -60;

  const N = closedCandles.length;
  const { wt1, wt2, warmup } = computeWaveSeries(closedCandles, channelLength, averageLength);
  if (!wt1.length) return EMPTY;

  const buys: number[] = [];
  const shorts: number[] = [];
  // Cross detection needs at least prior bar; the warmup gate comes from the
  // series (n1 + n2 bars for tci to settle; sma adds 3 more).
  for (let i = warmup; i < N; i++) {
    const a = wt1[i], b = wt2[i];
    const aPrev = wt1[i - 1], bPrev = wt2[i - 1];
    if (![a, b, aPrev, bPrev].every(Number.isFinite)) continue;
    const crossover = aPrev <= bPrev && a > b;
    const crossunder = aPrev >= bPrev && a < b;
    const t = closedCandles[i].time;
    if (crossover && b <= osLevel) buys.push(t);
    else if (crossunder && b >= obLevel) shorts.push(t);
  }
  return { buys, shorts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * LRC — LRBand vs CPR (round 30)
 *
 * BUY  fires when the LRBand's UPPER line crosses ABOVE the CPR top (TC)
 *      on the most recent closed bar.
 * SHORT fires when the LRBand's LOWER line crosses BELOW the CPR bottom (BC)
 *      on the most recent closed bar.
 *
 * Both LRBand and CPR are computed on the SAME closed-candle array passed
 * in by the chart, so the bar times line up 1:1 — we don't need any
 * step-hold projection here. CPR uses the chart's selected period
 * (`cprPeriod`) and exchange so the period boundaries match the existing
 * CPR overlay (NSE/MCX/DELTA session anchors).
 *
 * Alternation gate (matches FLLHH): after a BUY no further BUY fires
 * until a SHORT has fired (and vice versa). Prevents same-side stacking
 * across consecutive bars when the band sits above/below CPR for a run.
 *
 * Repaint-safe: cross is only confirmed at the closing bar — the forming
 * bar must not be passed in.
 * ──────────────────────────────────────────────────────────────────────── */
/** LRC marker — pinned to a specific price level so the chart can place
 *  the arrow exactly at the crossover line (CPR top for BUY, CPR bottom
 *  for SHORT) rather than relative to the candle bar. */
export interface LrcMarker { time: number; price: number; double?: boolean; }
export interface LrcSignalSet { buys: LrcMarker[]; shorts: LrcMarker[]; }

/**
 * LRC — POC breakout markers (rewritten).
 *   close crosses ABOVE C-POC top  → BUY  ★      (single star)
 *   close crosses BELOW C-POC bottom → SHORT ★    (single star)
 *   close crosses ABOVE H-POC top  → BUY  ★★     (double star)
 *   close crosses BELOW H-POC bottom → SHORT ★★   (double star)
 * "crosses" = previous candle's close on one side, current candle's close on the
 * other. The close is taken from a SELECTABLE candle type (Chart Settings → LRC),
 * independent of the chart's display candle type. C-POC / H-POC are computed on
 * RAW candles, matching the drawn C-POC / H-POC overlay bands (C-POC = the CTF
 * period; H-POC = one period higher). A single candle may fire both a * and a **.
 */
export function computeLRCSignals(
  rawCandles: CandleRow[],
  cprPeriod: CprPeriod,
  exchange?: string,
  candleType: CandleType = 'Regular',
): LrcSignalSet {
  if (rawCandles.length < 5) return { buys: [], shorts: [] };

  // Close per the chosen candle type (LRC's own setting).
  const typed = transformCandles(rawCandles, candleType);
  const closeByT = new Map<number, number>();
  for (const c of typed) closeByT.set(c.time, c.close);

  const hPeriod: CprPeriod = cprPeriod === 'D' ? 'W' : 'M';
  const cpoc = computePocPerPeriod(rawCandles, cprPeriod, exchange);
  const hpoc = computePocPerPeriod(rawCandles, hPeriod, exchange);
  const cpocByT = new Map<number, { top: number; bottom: number }>();
  for (const p of cpoc) cpocByT.set(p.time, { top: p.top, bottom: p.bottom });
  const hpocByT = new Map<number, { top: number; bottom: number }>();
  for (const p of hpoc) hpocByT.set(p.time, { top: p.top, bottom: p.bottom });

  const buys: LrcMarker[] = [];
  const shorts: LrcMarker[] = [];

  for (let i = 1; i < rawCandles.length; i++) {
    const tCur = rawCandles[i].time, tPrev = rawCandles[i - 1].time;
    const cl = closeByT.get(tCur), clPrev = closeByT.get(tPrev);
    if (cl == null || clPrev == null || !Number.isFinite(cl) || !Number.isFinite(clPrev)) continue;

    const cp = cpocByT.get(tCur), cpPrev = cpocByT.get(tPrev);
    if (cp && cpPrev && [cp.top, cp.bottom, cpPrev.top, cpPrev.bottom].every(Number.isFinite)) {
      if (clPrev < cpPrev.top && cl > cp.top) buys.push({ time: tCur, price: cp.top, double: false });
      else if (clPrev > cpPrev.bottom && cl < cp.bottom) shorts.push({ time: tCur, price: cp.bottom, double: false });
    }
    const hp = hpocByT.get(tCur), hpPrev = hpocByT.get(tPrev);
    if (hp && hpPrev && [hp.top, hp.bottom, hpPrev.top, hpPrev.bottom].every(Number.isFinite)) {
      if (clPrev < hpPrev.top && cl > hp.top) buys.push({ time: tCur, price: hp.top, double: true });
      else if (clPrev > hpPrev.bottom && cl < hp.bottom) shorts.push({ time: tCur, price: hp.bottom, double: true });
    }
  }

  return { buys, shorts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * Blast — C-POC breakout, gated to active Inside/Engulfing band windows.
 *
 * The Blast indicator draws Daily/Weekly bands around inside (HA) / engulfing
 * (Regular) candles (see utils/blast.ts). While ANY band is active at a bar, a
 * fresh close-cross of the C-POC band fires a directional signal:
 *
 *   BUY   = prevClose <= C-POC top    AND close > C-POC top    (and bar in window)
 *   SHORT = prevClose >= C-POC bottom AND close < C-POC bottom (and bar in window)
 *
 * C-POC is the per-period volume-profile band for the chart's CPR period
 * (computePocPerPeriod) — same source the LRC indicator's ★ uses. The close is
 * read from a selectable candle type (defaults to Regular), independent of the
 * chart's display candle type. Repaint-safe: feed closed candles only.
 * ──────────────────────────────────────────────────────────────────────── */
export function computeBlastSignals(
  rawCandles: CandleRow[],
  windows: BlastWindow[],
  cprPeriod: CprPeriod,
  exchange?: string,
  candleType: CandleType = 'Regular',
): SignalSet {
  if (rawCandles.length < 5 || !windows.length) return EMPTY;

  const typed = transformCandles(rawCandles, candleType);
  const closeByT = new Map<number, number>();
  for (const c of typed) closeByT.set(c.time, c.close);

  const cpoc = computePocPerPeriod(rawCandles, cprPeriod, exchange);
  const cpocByT = new Map<number, { top: number; bottom: number }>();
  for (const p of cpoc) cpocByT.set(p.time, { top: p.top, bottom: p.bottom });

  const buys: number[] = [];
  const shorts: number[] = [];

  for (let i = 1; i < rawCandles.length; i++) {
    const tCur = rawCandles[i].time, tPrev = rawCandles[i - 1].time;
    if (!isInBlastWindow(tCur, windows)) continue;
    const cl = closeByT.get(tCur), clPrev = closeByT.get(tPrev);
    if (cl == null || clPrev == null || !Number.isFinite(cl) || !Number.isFinite(clPrev)) continue;
    const cp = cpocByT.get(tCur), cpPrev = cpocByT.get(tPrev);
    if (!cp || !cpPrev || ![cp.top, cp.bottom, cpPrev.top, cpPrev.bottom].every(Number.isFinite)) continue;
    if (clPrev <= cpPrev.top && cl > cp.top) buys.push(tCur);
    else if (clPrev >= cpPrev.bottom && cl < cp.bottom) shorts.push(tCur);
  }

  return { buys, shorts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * AS — Absolute Strength Histogram v3.1 (port of jh's ASH pinescript)
 *
 * All inputs hardcoded per the source defaults:
 *   Length = 21 (period of evaluation), Smooth = 7 (period of smoothing),
 *   src_type = "Close" (so Price = close).
 *
 *   Bulls     = close - lowest(close, 21)
 *   Bears     = highest(close, 21) - close
 *   AvgBulls  = wma(Bulls, 21);    AvgBears = wma(Bears, 21)
 *   SmthBulls = wma(AvgBulls, 7);  SmthBears = wma(AvgBears, 7)
 *
 *   is_dark_green = SmthBulls >= SmthBulls[1]   (bull-line rising)
 *   is_dark_red   = SmthBears >= SmthBears[1]   (bear-line rising)
 *
 *   BUY    = is_dark_green AND NOT is_dark_red
 *   SHORT  = is_dark_red   AND NOT is_dark_green
 *
 * De-duplicated via `last_sig`: only the FIRST bar of a new BUY (or SHORT)
 * episode fires; no repeats until the opposite signal triggers. Repaint-safe
 * because computation runs on closed bars only.
 * ──────────────────────────────────────────────────────────────────────── */
function wma(src: number[], length: number): number[] {
  // ta.wma(src, length): weights are length, length-1, …, 1 (most recent
  // bar gets the highest weight). Sum of weights = length*(length+1)/2.
  const out: number[] = new Array(src.length).fill(NaN);
  const denom = (length * (length + 1)) / 2;
  for (let i = length - 1; i < src.length; i++) {
    let num = 0;
    for (let k = 0; k < length; k++) {
      num += src[i - k] * (length - k);
    }
    out[i] = num / denom;
  }
  return out;
}

export function computeASHSignals(closedCandles: CandleRow[]): SignalSet {
  const Length = 21;
  const Smooth = 7;
  const N = closedCandles.length;
  if (N < Length * 2 + Smooth + 2) return EMPTY;

  const closes = closedCandles.map(c => c.close);

  // Bulls = close - lowest(close, Length); Bears = highest(close, Length) - close.
  const bulls: number[] = new Array(N).fill(0);
  const bears: number[] = new Array(N).fill(0);
  for (let i = Length - 1; i < N; i++) {
    let lo = closes[i], hi = closes[i];
    for (let k = i - Length + 1; k <= i; k++) {
      if (closes[k] < lo) lo = closes[k];
      if (closes[k] > hi) hi = closes[k];
    }
    bulls[i] = closes[i] - lo;
    bears[i] = hi - closes[i];
  }

  const avgBulls = wma(bulls, Length).map(v => Number.isFinite(v) ? v : 0);
  const avgBears = wma(bears, Length).map(v => Number.isFinite(v) ? v : 0);
  const smthBulls = wma(avgBulls, Smooth);
  const smthBears = wma(avgBears, Smooth);

  const buys: number[] = [];
  const shorts: number[] = [];
  let lastSig: 0 | 1 | -1 = 0;

  // Warmup: skip until both smoothed series are valid AND we have a prior
  // bar for the >= comparison. 2*Length + Smooth covers the chained WMAs.
  const warmup = Length * 2 + Smooth;
  for (let i = warmup; i < N; i++) {
    const sb  = smthBulls[i],  sbPrev = smthBulls[i - 1];
    const sbr = smthBears[i],  sbrPrev = smthBears[i - 1];
    if (![sb, sbPrev, sbr, sbrPrev].every(Number.isFinite)) continue;

    const isDarkGreen = sb  >= sbPrev;
    const isDarkRed   = sbr >= sbrPrev;
    const buyRaw   = isDarkGreen && !isDarkRed;
    const shortRaw = isDarkRed   && !isDarkGreen;

    const t = closedCandles[i].time;
    if (buyRaw && lastSig !== 1) {
      buys.push(t);
      lastSig = 1;
    } else if (shortRaw && lastSig !== -1) {
      shorts.push(t);
      lastSig = -1;
    }
  }

  return { buys, shorts };
}
